import React, { useEffect, useRef, useState } from 'react';

declare global {
  interface Window { LightweightCharts: any; }
}


const SYMBOLS = ['RELIANCE', 'HDFC BANK', 'TCS', 'INFY'];
const INTERVALS = [
  { label: '1m', value: '1 minute' },
  { label: '5m', value: '5 minutes' },
  { label: '15m', value: '15 Minutes' },
  { label: '1h', value: '1 Hour' },
  { label: '1D', value: '1 Day' },
  { label: '1W', value: '1 Week' },
  { label: '1M', value: '1 Month' },
];

// ... (keep top of file, SYMBOLS, and INTERVALS as is)

const Chart = () => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const candleSeriesRef = useRef<any>(null);
  const wsRef = useRef<WebSocket | null>(null); 

  const [selectedSymbol, setSelectedSymbol] = useState('RELIANCE');
  const [selectedInterval, setSelectedInterval] = useState('1m');
  const [search, setSearch] = useState('RELIANCE');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isIntervalOpen, setIsIntervalOpen] = useState(false);
  const [hoverData, setHoverData] = useState<any>(null);


  // 1. Initialize Chart (Run once on mount)
  useEffect(() => {
    if (!window.LightweightCharts || !chartContainerRef.current) return;
    const socket = new WebSocket('ws://localhost:8080');  
    wsRef.current = socket;
    wsRef.current.onopen = () => {
        console.log('Connected to Market Data');
        // Clear existing data when switching symbol/interval to prevent mixing
        if (candleSeriesRef.current) {
          candleSeriesRef.current.setData([]); 
        }
        if (!wsRef.current) {
          console.log("WEBSOCKET not open");
          return;
        } 
    };
// Inside the first useEffect for Chart initialization
    const chart = window.LightweightCharts.createChart(chartContainerRef.current, {
      layout: { 
        background: { color: '#ffffff' }, // White background
        textColor: '#4a4a4a'            // var(--g-600)
      },
      grid: { 
        vertLines: { color: '#f2f2f2' }, // var(--g-100)
        horzLines: { color: '#f2f2f2' } 
      },
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight || 500,
      timeScale: { borderColor: '#d9d9d9', timeVisible: true }, // var(--g-200)
    });

    // Update the series colors
    const candleSeries = chart.addSeries(window.LightweightCharts.CandlestickSeries, {
      upColor: '#3ecf8e',   // var(--up)
      downColor: '#f87171', // var(--down)
      borderVisible: false,
      wickUpColor: '#3ecf8e', 
      wickDownColor: '#f87171',
    });

    chart.subscribeCrosshairMove((param: any) => {
      if (param.time) setHoverData(param.seriesData.get(candleSeries));
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;

    const handleResize = () => {
      chart.applyOptions({ 
        width: chartContainerRef.current?.clientWidth, 
        height: chartContainerRef.current?.clientHeight 
      });
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
      if (wsRef.current) {
        console.log("Closing WebSocket connection");
        wsRef.current.onclose = null; // Prevent accidental retry logic
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);



  useEffect(() => {
    const socket = wsRef.current;
    if (!socket) { 
      console.log("WEBSOCKET not open");
      return;
    }

    if (candleSeriesRef.current) {
      candleSeriesRef.current.setData([]); 
    }

    if (socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ symbol: selectedSymbol, interval: selectedInterval }));
    } else {
      socket.addEventListener('open', () => {
            socket.send(JSON.stringify({ symbol: selectedSymbol, interval: selectedInterval }));
      }, { once: true }); // Wait for connection
    }
    
    socket.onmessage = (event:any) => {
      try {
        const data = JSON.parse(event.data);

        const formattedTick = {
          time: data.time,
          open: Number(data.open),
          high: Number(data.high),
          low: Number(data.low),
          close: Number(data.close)
        };

        if (candleSeriesRef.current) {
          candleSeriesRef.current.update(formattedTick);
          setHoverData(formattedTick);
        }
      } catch (err) {
        console.error("Error updating chart:", err);
      }
    };
      
  }, [selectedSymbol, selectedInterval]);

useEffect(() => {
  const closeMenus = () => {
    setIsDropdownOpen(false);
    setIsIntervalOpen(false);
  };

  // Close when clicking anywhere on the window
  window.addEventListener('mousedown', closeMenus);
  
  return () => window.removeEventListener('mousedown', closeMenus);
}, []);

  const filteredSymbols = search === selectedSymbol || search === '' 
    ? SYMBOLS 
    : SYMBOLS.filter(s => s.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="chart-wrapper">
      <div className="chart-controls-row">

      <div className="chart-control-item" style={{ position: 'relative' }}>
        <input 
          type="text" 
          value={search} 
          onFocus={() => setIsDropdownOpen(true)}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsDropdownOpen(true);
          }}
          placeholder="SEARCH..."
        />
        <span style={{fontSize:'8px', marginLeft:'4px'}}>▼</span>

        {isDropdownOpen && (
          <div className="dropdown-list">
            {filteredSymbols.map(sym => (
              <div 
                key={sym} 
                className="dropdown-item" 
                onMouseDown={() => {
                  setSelectedSymbol(sym);
                  setSearch(sym);
                  setIsDropdownOpen(false);
                }}
              >
                {sym}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="chart-control-item" style={{ position: 'relative' }}>
        <button 
          onClick={(e) => {
            e.stopPropagation();
            setIsIntervalOpen(!isIntervalOpen);
            setIsDropdownOpen(false); // Close other menu
          }}
        >
          {selectedInterval} <span style={{fontSize:'8px', marginLeft:'4px'}}>▼</span>
        </button>

        {isIntervalOpen && (
          <div className="dropdown-list">
            {INTERVALS.map((int) => (
              <div 
                key={int.value} 
                className="dropdown-item" 
                onMouseDown={() => {
                  setSelectedInterval(int.label);
                  setIsIntervalOpen(false);
                }}
              >
                {int.label} <span style={{fontSize:'9px', color:'var(--g-400)', marginLeft:'auto'}}>{int.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>

        <div className="chart-ohlc-legend">
          {hoverData ? (
            <div className="legend-row">
              <span className="l-item">O <em className="val">{hoverData.open?.toFixed(2)}</em></span>
              <span className="l-item">H <em className="val up">{hoverData.high?.toFixed(2)}</em></span>
              <span className="l-item">L <em className="val down">{hoverData.low?.toFixed(2)}</em></span>
              <span className="l-item">C <em className="val">{hoverData.close?.toFixed(2)}</em></span>
            </div>
          ) : (
            <span style={{fontStyle:'italic', color:'#909090'}}>Connecting...</span>
          )}
        </div>
      </div>

      <div ref={chartContainerRef} style={{width:'100%', height:'100%'}} />
    </div>
  );
};

export default Chart;